/** Severity of a staff notice — drives placement, reach, colour and icon. */
export type NoticeLevel = 'outage' | 'warning' | 'maintenance' | 'info' | 'resolved';

/** Order the levels are offered in, most severe first. */
export const NOTICE_LEVELS: NoticeLevel[] = [
    'outage',
    'warning',
    'maintenance',
    'info',
    'resolved',
];

export const NOTICE_LEVEL_LABELS: Record<NoticeLevel, string> = {
    outage: 'Outage',
    warning: 'Warning',
    maintenance: 'Maintenance',
    info: 'Info',
    resolved: 'Resolved',
};

/** Headline cap — it has to stay on one line next to the level chip. */
export const NOTICE_HEADLINE_MAX = 60;

/** A staff-authored global notice (`backoffice.notices`). */
export interface Notice {
    id: string;
    headline: string;
    detail: string | null;
    level: NoticeLevel;
    area: string | null;
    link: string | null;
    is_active: boolean;
    displayed_at: string | null;
    created_at: string;
}

/** The editable half of a notice — what the staff composer submits. */
export type NoticeDraft = Pick<Notice, 'headline' | 'detail' | 'level' | 'area' | 'link'>;

/** Where a notice renders: a solid bar above the chrome, or an in-content strip. */
export type NoticePlacement = 'bar' | 'strip';

/** How far a notice follows the user: every page, or Home only. */
export type NoticeReach = 'all' | 'home';

/** Only an outage takes over the chrome; everything else is an in-content strip. */
export function placementFor(level: NoticeLevel): NoticePlacement {
    return level === 'outage' ? 'bar' : 'strip';
}

/**
 * Warning and maintenance follow the user everywhere because they change how
 * far the data on screen can be trusted. Info and resolved stay on Home:
 * announcing product news app-wide trains people to dismiss banners reflexively,
 * which is what destroys the outage bar's impact when it matters.
 */
export function reachFor(level: NoticeLevel): NoticeReach {
    return level === 'info' || level === 'resolved' ? 'home' : 'all';
}

const RESOLVED_TTL_MS = 24 * 60 * 60 * 1000;

/** A `resolved` notice stops showing 24h after it went on display. */
export function hasExpired(notice: Notice, now: number = Date.now()): boolean {
    if (notice.level !== 'resolved' || !notice.displayed_at) return false;
    return now - Date.parse(notice.displayed_at) > RESOLVED_TTL_MS;
}

/**
 * The single decision function: does `notice` belong in this placement, on this
 * page? Keeping it here is what stops level checks spreading through the layout.
 */
export function isNoticeVisible(
    notice: Notice,
    placement: NoticePlacement,
    pathname: string,
    now?: number
): boolean {
    if (placementFor(notice.level) !== placement) return false;
    if (reachFor(notice.level) === 'home' && pathname !== '/') return false;
    return !hasExpired(notice, now);
}
